import { useMemo, useRef, useState } from 'react';
import { mergeProps, useButton, useFocusRing } from 'react-aria';

import { getButtonImage } from '../controls/button/getButtonImage';

import { useHover } from '../../hooks/useHover.js';

import styles from './DeviceQuickAction.module.css';

import type { DeviceQuickActionProps } from './DeviceQuickAction.types';
import { getMediaDeviceQuickActionColors } from './DeviceQuickActionColors';
import type { DeviceQuickActionStyle } from './DeviceQuickActionColors';
import { DeviceQuickActionIcon } from './DeviceQuickActionIcon';

export function DeviceQuickActionMediaButton(props: DeviceQuickActionProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const [pulseVersion, setPulseVersion] = useState(0);
  const focusRing = useFocusRing({});
  const hover = useHover({});
  const button = useButton(
    {
      'aria-label': props.label,
      isDisabled: props.isDisabled === true,
      onPress() {
        if (props.isDisabled === true) {
          return;
        }

        setPulseVersion((currentPulseVersion) => {
          return currentPulseVersion + 1;
        });
        props.onPress();
      },
    },
    ref,
  );

  const buttonProps =
    props.isDisabled === true
      ? mergeProps(button.buttonProps, focusRing.focusProps)
      : mergeProps(button.buttonProps, focusRing.focusProps, hover.hoverProps);
  const buttonImage =
    props.capabilityObj != null
      ? getButtonImage(props.capabilityObj.id, props.capabilityObj)
      : null;
  const style = useMemo(() => {
    const colors = getMediaDeviceQuickActionColors({
      isActive: button.isPressed,
      isHovered: false,
    });
    const hoverColors = getMediaDeviceQuickActionColors({
      isActive: button.isPressed,
      isHovered: true,
    });
    const pulseColors = getMediaDeviceQuickActionColors({
      isActive: true,
      isHovered: false,
    });

    return {
      '--prop-background-color': colors.backgroundColor,
      '--prop-border-color': colors.borderColor,
      '--prop-hover-background-color': hoverColors.backgroundColor,
      '--prop-hover-border-color': hoverColors.borderColor,
      '--prop-icon-color': colors.iconColor,
      '--prop-icon-size': colors.iconSize,
      '--prop-hover-icon-color': hoverColors.iconColor,
      '--prop-outline-color': colors.outlineColor,
      '--prop-overlay-color': colors.overlayColor,
      '--prop-pulse-color': pulseColors.iconColor,
      '--prop-hover-overlay-color': hoverColors.overlayColor,
    } as DeviceQuickActionStyle;
  }, [button.isPressed]);

  return (
    <button
      {...buttonProps}
      ref={ref}
      className={styles.root}
      style={style}
      data-is-focus-visible={focusRing.isFocusVisible === true ? '' : undefined}
      data-is-hovered={props.isDisabled === true ? false : hover.isHovered}
      data-is-pressed={props.isDisabled !== true && button.isPressed === true ? '' : undefined}
      data-variant={props.variant}
      title={props.label}
      type="button"
    >
      {pulseVersion > 0 ? (
        <span key={pulseVersion} aria-hidden={true} className={styles.pulse} />
      ) : null}

      <DeviceQuickActionIcon
        iconObj={buttonImage?.iconObj}
        source={buttonImage?.url}
        useIconObj={buttonImage?.useIconObj === true}
      />
    </button>
  );
}
